import { Injectable } from "@angular/core"
import { ConnectionService } from '@services/connection-service';
import { AppConfig } from '../../app/app.config';
import { Router} from '@angular/router';
import { InstantCheckupService } from '@services/instant-checkup-service';
import { ReAcquisitionUserQueryBottomSheetComponent } from "@shared/bottom-sheet-layouts/re-acquisition-user-query-bottom-sheet/re-acquisition-user-query-bottom-sheet.component";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { ReAcquisitionWaitingForCallSheetComponent } from "@shared/bottom-sheet-layouts/re-acquisition-waiting-for-call-sheet/re-acquisition-waiting-for-call-sheet.component";
import { RequestPhotoBottomSheetComponent } from "@shared/bottom-sheet-layouts/request-photo-bottom-sheet/request-photo-bottom-sheet.component";
import { EventLoggerService } from "@services/event-logger-service";
import { TimeService } from "@services/time-service";
import { resetCacheByName } from "@services/connection-service/cache-interceptor";
export type ReAcquisitionBannerType = 'reAcquisitionBannerReady' | 'waitingForReAcquisitionCall' | 'missedReAcquisitionCall' | 'reAcquisitionCheckupPending';

interface ReAcquisitionResponse {
  totalReAcquisitionResponse: number;
  reAcquisitionResponseYes?: boolean;
  lastReAcquisititionResponseTime?: string;
}
@Injectable({
  providedIn: "root"
})

export class ReAcquisitionService {
  regimens: Table.Regimen[] = [];
  isPhotoTaken: boolean = false;
  isPendingCallCreated: boolean = false;
  MISSED_CALL_WINDOW_DAYS: number = 2;

  constructor (
    private readonly connectionService: ConnectionService,
    private readonly appConfig: AppConfig,
    private readonly router: Router,
    private readonly instantCheckupService: InstantCheckupService,
    private readonly bottomSheet: MatBottomSheet,
    private readonly eventLogger: EventLoggerService,
    private readonly timeService: TimeService,
  ) {}

  async navigateToReAcquisitionFlow(bannerType: ReAcquisitionBannerType, user: Table.User): Promise<void> {
    switch (bannerType) {
      case 'missedReAcquisitionCall':
        this.router.navigate(['/support']);
        break;

      case 'reAcquisitionBannerReady':
        this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'reacquisition_start_checkup'}));
        await this.updateUserReAcquisitionResponse(user);
        this.navigateBasedOnInstantCheckup(user);
        break;

      case 'reAcquisitionCheckupPending':
        this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'reacquisition_checkup_pending'}));
        await this.updateUserReAcquisitionResponse(user);
        this.router.navigate(['/re-acquisition/user-query-v2']);
        break;

      default:
        break;
    }
  }

  async navigateBasedOnInstantCheckup(user: Table.User): Promise<void> {
    const isPendingCallCreated = this.getIsPendingCallCreated();
    if (isPendingCallCreated) {
      this.router.navigate(['/re-acquisition/waiting-for-call']);
      return;
    }
    const type = Object.keys(this.appConfig.Shared.InstantCheckup.Type).filter((type_: string): boolean => {
      return ![this.appConfig.Shared.InstantCheckup.Type.INVALID,
        this.appConfig.Shared.InstantCheckup.Type.REPORT,
        this.appConfig.Shared.InstantCheckup.Type.BODY,
        this.appConfig.Shared.InstantCheckup.Type.OTHER].includes(type_);
    });
    const instantCheckup = await this.instantCheckupService.getInstantCheckupOfType(user, 7, type);
    if (instantCheckup.length > 0) {
      this.router.navigate(['/re-acquisition/user-query-v2']);
      return;
    }
    this.router.navigate(['/re-acquisition/concern-selection']);
  }

  async updateUserReAcquisitionResponse(user: Table.User): Promise<void> {
    const currentResponse = user.get('reAcquisitionResponse') || { totalReAcquisitionResponse: 0 };
    const updatedResponse: ReAcquisitionResponse = {
      totalReAcquisitionResponse: (currentResponse as any).totalReAcquisitionResponse + 1,
      reAcquisitionResponseYes: true,
      lastReAcquisititionResponseTime: new Date().toISOString(),
    };

    user.set('reAcquisitionResponse', updatedResponse as any);
    await user.save();
  }

  async createReAcquisitionPendingCall(userQuery: string): Promise<void> {
    await this.connectionService.createPendingCall({
      type: this.appConfig.Shared.PendingCall.Type.RE_ACQUISITION_CALL,
      message: userQuery,
    });
    resetCacheByName('findOnePendingCall');
    this.setIsPendingCallCreated(true);
  }

  openRequestPhotoBottomSheet(checkupType: string, imagePath: string, callback: () => void): void {
    const requiredPhotoTypes = this.instantCheckupService.getRequiredPhotoTypesForReacquisition(checkupType);
    const isSideFaceRequired = requiredPhotoTypes.includes(this.appConfig.Shared.InstantCheckup.Type.LEFT_SIDE_FACE) || requiredPhotoTypes.includes(this.appConfig.Shared.InstantCheckup.Type.RIGHT_SIDE_FACE);
    this.bottomSheet.open(RequestPhotoBottomSheetComponent, {
      data: {
        requiredPhotoType: isSideFaceRequired ? this.appConfig.Shared.InstantCheckup.Type.SIDE_FACE : this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE,
        currentPhotoType: checkupType,
        currentImagePath: imagePath,
        onContinue: (): void => {
          this.setIsPhotoTaken(true);
          callback();
        },
      }
    })
  }

  openReacquisitionUserQueryBottomSheet(user: Table.User): void {
    this.bottomSheet.open(ReAcquisitionUserQueryBottomSheetComponent, {
      data: {
        user,
        onContinue: async (userQuery: string): Promise<void> =>  {
          await this.createReAcquisitionPendingCall(userQuery);
          this.openReacquisitionWaitingForCallBottomSheet(user);
        }
      }
    })
  }

  openReacquisitionWaitingForCallBottomSheet(user: Table.User): void {
    this.bottomSheet.open(ReAcquisitionWaitingForCallSheetComponent, {
      data: {
        user,
        onContinue: (): void => {
          this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
        }
      }
    })
  }

  setIsPhotoTaken(isPhotoTaken: boolean): void {
    this.isPhotoTaken = isPhotoTaken;
  }

  getIsPhotoTaken(): boolean {
    return this.isPhotoTaken;
  }

  setIsPendingCallCreated(isPendingCallCreated: boolean): void {
    this.isPendingCallCreated = isPendingCallCreated;
  }

  getIsPendingCallCreated(): boolean {
    return this.isPendingCallCreated;
  }

  async decideReAcquisitionBannerType(user: Table.User): Promise<ReAcquisitionBannerType | null> {
    const lastReAcquisitionCall = await this.getLastReAcquisitionCall(user);
    const status = lastReAcquisitionCall?.get('status');

    if (status === this.appConfig.Shared.PendingCall.Status.CallCompleted) {
      return null;
    }

    const instantCheckups = await this.getValidInstantCheckups(user);

    if (!lastReAcquisitionCall) {
      return this.handleNoReAcquisitionCall(instantCheckups);
    }

    if (status === this.appConfig.Shared.PendingCall.Status.Requested) {
      return 'waitingForReAcquisitionCall';
    }

    return this.handleMissedCall(lastReAcquisitionCall);
  }

  private async getLastReAcquisitionCall(user: Table.User) {
    return await this.connectionService.findOnePendingCall({
      where: {
        user: user,
        type: this.appConfig.Shared.PendingCall.Type.RE_ACQUISITION_CALL,
        createdAt: { $gte: this.timeService.removeMonths(new Date(), 1) },
      },
      descending: 'requestTime',
      project: ['status', 'requestTime'],
    });
  }

  private async getValidInstantCheckups(user: Table.User) {
    const validTypes = this.instantCheckupService.getValidInstantCheckupTypes();
    return await this.instantCheckupService.getInstantCheckupOfType(user, 7, validTypes);
  }

  private handleNoReAcquisitionCall(instantCheckups: any[]): ReAcquisitionBannerType {
    if (instantCheckups.length > 0) {
      return 'reAcquisitionCheckupPending';
    }
    return 'reAcquisitionBannerReady';
  }

  private handleMissedCall(lastReAcquisitionCall: any): ReAcquisitionBannerType | null {
    const daysSinceCallRequested = this.timeService.differenceInDays(
      new Date(),
      lastReAcquisitionCall.get('requestTime')
    );

    if (daysSinceCallRequested <= this.MISSED_CALL_WINDOW_DAYS) {
      this.eventLogger.cleverTapEvent('HP_reacquisition_call_RNR_rendered');
      return 'missedReAcquisitionCall';
    }
    return null;
  }
}
